import DTMFAudio from '@/libs/dtmfAudio'
import newCallEvents from './newCallEvents'
import { holdModifier } from 'sip.js/lib/platform/web/modifiers'
import { SessionState, UserAgent, Registerer, Inviter, Session, SIPExtension, Messager, Subscriber } from 'sip.js'
import { getVersion, isDebug } from '@/libs/version'
import { OFFLINE, PAUSED } from '@/constants/agentStatuses'
import convert from 'xml-js'

let userAgent = null
let subscriber = null
let registerer = null
let sipAgentCached = null

const xmlHeader = '<?xml version="1.0" encoding="UTF-8"?>\n'

DTMFAudio.init()

export default () => {
  let domain = null
  let extension = null

  const connect = async (provision, sipEvents) => {
    if (!provision?.host) return null

    domain = provision.host
    extension = provision.extension

    const transportOptions = {
      server: `wss://${domain}/ws`,
      traceSip: isDebug()
    }

    const uri = UserAgent.makeURI(`sip:${extension}@${domain}`)
    const sipjsId = `ramal-${extension}-`
    let connected = false

    return new Promise((resolve, reject) => {
      const options = {
        uri,
        sipjsId,
        userAgentString: `VonixPhone/${getVersion()}`,
        transportOptions,
        sipExtension100rel: SIPExtension.Required,
        sipExtensionReplaces: SIPExtension.Required,
        authorizationUsername: extension,
        authorizationPassword: provision.password,
        logLevel: isDebug() ? 'debug' : 'error',
        sipExtensionExtraSupported: ['eventlist'],
        sessionDescriptionHandlerFactoryOptions: {
          peerConnectionConfiguration: {
            iceServers: [],
            bundlePolicy: 'max-bundle',
            rtcpMuxPolicy: 'require'
          }
        },

        delegate: Object.assign({}, sipEvents, {
          async onConnect () {
            console.log('Conectado ao SIP com sucesso.')

            try {
              await registerer.register()
              await wait(300)

              const targetURI = UserAgent.makeURI(`sip:list@${domain}`)
              const eventType = 'presence' // https://www.iana.org/assignments/sip-events/sip-events.xhtml

              subscriber = new Subscriber(userAgent, targetURI, eventType)

              subscriber.delegate = {
                onNotify: (notify) => {
                  notify.accept()

                  const extensions = String(notify.request?.toString() || '')
                    .split(xmlHeader)
                    .filter((data) => data.startsWith('<presence entity='))
                    .map((data) => JSON.parse(convert.xml2json(xmlHeader + data.substring(0, data.indexOf('</presence>\n') + 12))))
                    .reduce((acc, data) => {
                      const status = data.elements.at(0).elements.find((i) => i.name === 'note').elements.at(0).text
                      const item = data.elements.at(0).elements.find((i) => i.name === 'tuple')
                      const id = item.attributes.id
                      const info = item.elements.find((i) => i.name === 'status').elements.find((i) => i.name === 'basic').elements.at(0).text
                      acc[id] = { id, status, info }
                      return acc
                    }, {})

                  sipEvents.onNotify(extensions)
                }
              }

              subscriber.subscribe()
              requestStatusSIP()
            } catch (err) {
              await userAgent?.disconnect().catch(() => null)
              console.log('Erro ao registrar no SIP.', err)
              reject(err)
            }
          },

          onDisconnect (err) {
            console.warn('Desconectado do SIP.', err)
            subscriber.unsubscribe()
            sipEvents.onDisconnect(err)
          },

          onInvite (invitation) {
            if (invitation.isCanceled) return
            registerStateListeners(invitation)
            sipEvents.onInvite(invitation)
          },

          onMessage (msg) {
            const message = handleMessage(msg)
            if (message?.Event !== 'Agent') return sipEvents.onMessage(message)

            const agent = {
              paused: message.Status === PAUSED,
              logged: message.Status !== OFFLINE,
              status: message.Status,
              sipName: message.AgentName,
              queueList: removePenalty(message.QueueList)
            }

            if (message.AgentId) agent.id = message.AgentId
            if (message.PauseReason) agent.pauseReasonId = message.PauseReason

            sipAgentCached = agent

            if (!connected) {
              connected = true
              return resolve(agent)
            }

            sipEvents.onMessage(Object.assign(message, { agent }))
          }
        })
      }

      userAgent = new UserAgent(options)
      registerer = new Registerer(userAgent, { expires: 3600 })

      userAgent.start().catch(reject)
    })
  }

  const call = (destination, deviceId, queueId) => {
    const options = {}
    if (queueId) options.extraHeaders = [`X-Vonix-Queue-Id: ${queueId}`]
    return callExec(destination.includes('sip:') ? destination : `sip:${destination}@${domain}`, deviceId, options)
  }

  const callExec = (destination, deviceId, options = {}) => {
    const target = UserAgent.makeURI(destination)
    const inviter = new Inviter(userAgent, target, options)
    const events = registerStateListeners(inviter)

    inviter.invite({
      withoutSdp: true,

      sessionDescriptionHandlerOptions: {
        constraints: {
          video: false,
          audio: {
            channelCount: 1,
            echoCancellation: true,
            deviceId: { exact: deviceId || 'default' }
          }
        }
      },

      requestDelegate: {
        onTrying: events.emitTrying,
        onProgress: events.emitProgress,
        onAccept: events.emitAccept,
        onReject: events.emitReject,
        onRedirect: events.emitRedirect
      }
    })

    return inviter
  }

  const handleSession = (session, commit) => {
    session.stateChange.addListener((state) => {
      switch (state) {
        case SessionState.Established: return commit.CONNECTED(session.id)
        case SessionState.Terminated: return commit.ENDED(session.id)
        case SessionState.Establishing: return session instanceof Inviter ? commit.CONNECTING(session.id) : commit.RINGING(session.id)
      }
    })
  }

  const registerStateListeners = (session) => {
    const events = newCallEvents(session)

    session.stateChange.addListener((state) => {
      switch (state) {
        case SessionState.Initial: return events.emitInitial()
        case SessionState.Establishing: return events.emitEstablishing()
        case SessionState.Established: return events.emitEstablished()
        case SessionState.Terminating: return events.emitTerminating()
        case SessionState.Terminated: return events.emitTerminated()
      }
    })

    return events
      .onProgress(setupEarlyMedia)
      .onEstablished(setupRemoteMedia)
      .onTerminated(cleanupMedias)
      .onTerminated(session.events.destroy)
  }

  const transfer = (session, destination) => {
    if (destination instanceof Session) return session.refer(destination)
    const transferTarget = UserAgent.makeURI(`sip:${destination}@${domain}`)
    session.refer(transferTarget)
  }

  const disconnect = async (err) => {
    if (err) console.log({ err })

    if (registerer && registerer.state !== 'Terminated') {
      registerer.unregister()
      await wait(300)
      console.log('Unregister realizado com sucesso.')
    }

    registerer = null

    if (userAgent) {
      userAgent.stop()
      console.log('UserAgent parado com sucesso.')
    }

    userAgent = null
  }

  const wsReadyState = () => userAgent?.transport?.ws?.readyState === 1

  const requestStatusSIP = async (cnt = 0) => {
    let count = isNaN(cnt) ? 0 : Number(cnt)
    if (count) await wait(500)
    if (registerer.waiting) return requestStatusSIP(++count)
    if (registerer.state !== 'Registered') return requestStatusSIP(++count)
    const messager = new Messager(userAgent, UserAgent.makeURI(`sip:vonix@${domain}`), 'Action: AgentStatus')
    messager.message()
  }

  const accept = (session, input) => {
    session.accept({
      sessionDescriptionHandlerOptions: {
        constraints: {
          video: false,
          audio: {
            channelCount: 1,
            echoCancellation: true,
            deviceId: { exact: input }
          }
        }
      }
    })
  }

  const reconnect = () => {
    sipAgentCached = null

    return userAgent
      .reconnect()
      .then(() => subscriber.subscribe())
      .then(requestStatusSIP)
      .then(() => wait(1500))
      .then(() => sipAgentCached)
  }

  return {
    dtmf,
    call,
    hold,
    cancel,
    accept,
    hangup,
    connect,
    transfer,
    reconnect,
    disconnect,
    handleMute,
    conference,
    wsReadyState,
    handleHeaders,
    handleSession
  }
}

const cancel = (session, options = {}) => {
  if (session instanceof Inviter) return session?.cancel()
  session?.reject(options)
}

const dtmf = (session, dtmf) => {
  const duration = 80

  const body = {
    contentDisposition: 'render',
    contentType: 'application/dtmf-relay',
    content: `Signal=${dtmf}\r\nDuration=${duration}`
  }

  const requestOptions = { body }
  session?.info({ requestOptions })
}

const hangup = (session) => {
  session?.bye()
}

const hold = (session, hold) => {
  session?.invite({
    withoutSdp: true,
    sessionDescriptionHandlerModifiers: hold ? [holdModifier] : []
  })
}

const removePenalty = (queues) => queues?.split(',')?.map(queue => queue.replace(/-\d+$/, '')).join(',')

const handleMessage = (data) => {
  const incomingMessage = String(data?.incomingMessageRequest?.message?.body).trim()

  const message = `{${incomingMessage}}`
    .replace(/;/g, '", ')
    .replace(/([a-zA-Z]+):\s?/g, '"$1": "')
    .replace('}', '"}')

  try {
    return JSON.parse(message)
  } catch {
    return null
  }
}

const handleHeaders = (headers) => {
  const hasAutoAnswer = (
    headers['P-Auto-Answer'] &&
    headers['P-Auto-Answer'].length &&
    headers['P-Auto-Answer'][0].raw === 'normal'
  )

  const queueHeader = headers['X-Vonix-Queue-Id']
  const directionHeader = headers['X-Vonix-Mode']
  const callIdHeader = headers['X-Vonix-Call-Id']
  const protocolHeader = headers['X-Vonix-Protocol']

  let queue = queueHeader ? queueHeader[0].raw : null

  if (queue?.startsWith('iqda')) {
    const queueOriginalHeader = headers['X-Dialer-Original-Queue-Id']
    queue = queueOriginalHeader ? queueOriginalHeader[0].raw : null
  }

  const direction = directionHeader ? directionHeader[0].raw : null
  const callId = callIdHeader ? callIdHeader[0].raw : null
  const protocol = protocolHeader ? protocolHeader[0].raw : null

  return { hasAutoAnswer, queue, direction, callId, protocol }
}

const handleMute = (mute, session) => {
  const pc = session?.sessionDescriptionHandler?.peerConnection
  if (pc?.getSenders) {
    pc?.getSenders().forEach((sender) => {
      if (sender?.track) sender.track.enabled = !mute
    })
  } else {
    pc?.getLocalStreams().forEach((stream) => {
      stream?.getAudioTracks().forEach((track) => {
        track.enabled = !mute
      })
    })
  }
}

const conference = (sessions, initiate) => {
  const receivedTracks = []
  const context = new AudioContext()
  const allReceivedMediaStreams = new MediaStream()

  if (!initiate) return sessions.forEach(setupRemoteMedia)

  sessions.forEach((session) => {
    if ([null, undefined].includes(session)) return

    session.sessionDescriptionHandler.peerConnection.getReceivers().forEach((receiver) => {
      receivedTracks.push(receiver.track)
    })
  })

  sessions.forEach(function (session) {
    if (session !== null && session !== undefined) {
      const mixedOutput = context.createMediaStreamDestination()

      session.sessionDescriptionHandler.peerConnection.getReceivers().forEach(function (receiver) {
        receivedTracks.forEach(function (track) {
          if (initiate) {
            allReceivedMediaStreams.addTrack(receiver.track)
            if (receiver.track.id !== track.id && initiate) {
              const sourceStream = context.createMediaStreamSource(new MediaStream([track]))
              sourceStream.connect(mixedOutput)
            }
          }
        })
      })

      session.sessionDescriptionHandler.peerConnection.getSenders().forEach(function (sender) {
        const sourceStream = context.createMediaStreamSource(new MediaStream([sender.track]))
        sourceStream.connect(mixedOutput)
      })

      session.sessionDescriptionHandler.peerConnection.getSenders()[0].replaceTrack(mixedOutput.stream.getTracks()[0])
    }
  })

  const remoteAudio = document.getElementById('remoteAudio')
  if (!remoteAudio) return

  remoteAudio.srcObject = allReceivedMediaStreams
  remoteAudio.play()?.catch(err => console.log(err))
}

const setupEarlyMedia = (inviter) => {
  const sessionDescriptionHandler = Array.from(inviter.earlyMediaSessionDescriptionHandlers.values()).pop()
  if (!sessionDescriptionHandler) return DTMFAudio.playCustom('ringback')

  setupAudioMedia(
    `audio_early_${inviter.id}`,
    sessionDescriptionHandler.peerConnection
  )
}

const setupRemoteMedia = (session) => {
  cleanupMedias(session)
  setupAudioMedia(`audio_remote_${session.id}`, session.sessionDescriptionHandler.peerConnection)
}

const setupAudioMedia = (audioId, peerConnection) => {
  const audio = factoryAudio(audioId)
  audio.srcObject = getRemoteStream(peerConnection)
  audio.play().catch(() => console.error('play was rejected'))
}

const getRemoteStream = (peerConnection) => {
  if (!peerConnection.getReceivers) return peerConnection.getRemoteStreams()[0]

  const remoteStream = new global.window.MediaStream()
  peerConnection.getReceivers().forEach(({ track }) => track && remoteStream.addTrack(track))
  return remoteStream
}

const factoryAudio = (remoteAudioId) => {
  let remoteAudio = document.getElementById(remoteAudioId)

  if (!remoteAudio) {
    remoteAudio = document.createElement('audio')
    remoteAudio.id = remoteAudioId
    document.body.appendChild(remoteAudio)
  }

  return document.getElementById(remoteAudioId)
}

const cleanupMedias = (session) => {
  const audioMedias = [`audio_remote_${session.id}`, `audio_early_${session.id}`]
  audioMedias.forEach(cleanupMedia)
}

const cleanupMedia = (audioId) => {
  DTMFAudio.stop()
  const audio = document.getElementById(audioId)
  if (!audio) return
  audio.srcObject = null
  audio.pause()
  audio.remove()
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
